import React, {ChangeEvent, FC, useState} from 'react';
import Panel from '@/components/Panels/common/Panel';
import {PANEL_WIDTH} from '@/config';
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation';
import Button from '@mui/material/Button';

import {useTranslation} from 'react-i18next';
import {DocumentationMode} from '@/components/Panels/Parcel/Documentation/index';
import Stack from '@mui/material/Stack';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import Typography from '@mui/material/Typography';
import SubTitle from '@/components/forms/SubTitle';
import ListIcon from '@mui/icons-material/List';
import TextField from '@mui/material/TextField';
import {ParcelDocumentCreation} from '@/domain/entities/ParcelDocument';
import UploadFileButton from '@/components/Panels/Parcel/Documentation/UploadFileButton';
import SendIcon from '@mui/icons-material/Send';

//TYPES
export type AddDocumentPanelProps = {
  selectedParcelUuid: string,
  onDocumentUpload: (dataForUpload: ParcelDocumentCreation) => void,
  onDocumentationModeChanged: (documentationMode: DocumentationMode) => void
};

const DocumentAddPanel: FC<AddDocumentPanelProps> = ({selectedParcelUuid, onDocumentUpload, onDocumentationModeChanged}) => {

  const initialDocument = {
    parcelUuid: selectedParcelUuid,
    file: undefined,
    description: undefined
  };
  
  const {t} = useTranslation();

  const [dataForUpload, setDataForUpload] = useState<ParcelDocumentCreation>(initialDocument);

  const handleDescriptionChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setDataForUpload({
      ...dataForUpload,
      description: e.target.value
    });
  };
  
  const handleDocumentChange = (file?: File) => {
    setDataForUpload({
      ...dataForUpload,
      file
    });
  };
  
  const handleExit = () => onDocumentationModeChanged(DocumentationMode.LIST);
  const handleDocumentUpload = () => {
    onDocumentUpload(dataForUpload);
    onDocumentationModeChanged(DocumentationMode.LIST);
  };

  return <Panel width={PANEL_WIDTH.addDocument}>
    <Stack direction="column">
      <Stack direction="row" sx={{alignItems: 'center', gap: 1, mb: 2}}>
        <NoteAddIcon sx={{fontSize: '28px', color: 'primary.main'}}/>
        <Typography variant="h6" sx={{color: 'primary.main', m: 0, p: 0}}>{t('addDocumentsPanel.title')}</Typography>
      </Stack>
    </Stack>
    <Stack sx={{gap: 2}}>
      <SubTitle label={t('addDocumentsPanel.description')} icon={<ListIcon sx={styles.infoIcon}/>}>
        <TextField
          placeholder={t('addDocumentsPanel.optionalDescription')}
          value={dataForUpload?.description || ''}
          multiline={true}
          maxRows={4}
          onChange={handleDescriptionChange}
          sx={{mt: 1}}
        />
      </SubTitle>
    </Stack>

    <Stack sx={{mt: 2}}>
      <UploadFileButton placeholder={dataForUpload && dataForUpload.file ? dataForUpload.file.name : t('addDocumentsPanel.uploadFile')} onFileUpload={handleDocumentChange}/>
    </Stack>
    <Stack direction="row" sx={{justifyContent: 'space-between'}}>
      <Button variant="outlined" startIcon={<CancelPresentationIcon/>} sx={styles.smallButton} onClick={handleExit}>
        {t('documentsPanel.exit')}
      </Button>  
      <Button disabled={!dataForUpload.file} variant="outlined" startIcon={<SendIcon/>} sx={styles.smallButton} onClick={handleDocumentUpload}>
        {t('documentsPanel.send')}
      </Button>
    </Stack>
  </Panel>;
};
export default DocumentAddPanel;

const styles = {
  smallButton: {
    width: '120px',
    mt: 2
  },
  infoIcon: {
    fontSize: '20px',
    color: 'primary.main'
  },
  bigButton: {
    mt: 2
  }
};